import { useCallback, useMemo, useState } from 'react';
import useSnackbar from 'hooks/useSnackbar';
import { useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Badge from '@mui/material/Badge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  FilePdfOutlined,
  HourglassOutlined
} from '@ant-design/icons';
import { CityBillsUploadProvider, useCityBillsUpload, useCityBillsUploadOptional } from './CityBillsUploadContext';
import { CityBillsUploaderProps } from './types';
import { DEFAULT_MAX_FILES, PDF_ACCEPT } from './constants';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatBuildingIds(ids: number[]): string {
  return ids.join(', ');
}

function StatusChip({ status }: { status: string }) {
  switch (status) {
    case 'success':
      return <Chip size="small" color="success" icon={<CheckCircleOutlined />} label="Success" />;
    case 'error':
      return <Chip size="small" color="error" icon={<CloseCircleOutlined />} label="Failed" />;
    case 'uploading':
      return <Chip size="small" color="primary" icon={<CloudUploadOutlined />} label="Uploading" />;
    default:
      return <Chip size="small" icon={<HourglassOutlined />} label="Pending" />;
  }
}

function UploadProgressListContent() {
  const { items, removeItem, clearCompleted, isUploading } = useCityBillsUpload();

  const hasCompleted = items.some((i) => i.status === 'success' || i.status === 'error');

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Upload progress</Typography>
        {hasCompleted && !isUploading && (
          <Button size="small" onClick={clearCompleted}>
            Clear finished
          </Button>
        )}
      </Stack>
      <List dense disablePadding>
        {items.map((item) => (
          <ListItem
            key={item.id}
            sx={{
              flexDirection: 'column',
              alignItems: 'stretch',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
              py: 1.5,
              px: 2
            }}
            secondaryAction={
              item.status !== 'uploading' ? (
                <IconButton edge="end" size="small" aria-label="remove" onClick={() => removeItem(item.id)}>
                  <CloseOutlined style={{ fontSize: 18 }} />
                </IconButton>
              ) : undefined
            }
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 4 }}>
              <Box component="span" sx={{ color: 'action.active', fontSize: 20, display: 'flex' }}>
                <FilePdfOutlined />
              </Box>
              <ListItemText
                primary={item.fileName}
                secondary={
                  <>
                    {formatFileSize(item.fileSize)}
                    {' · Property '}
                    {item.property_id}
                    {' · Buildings '}
                    {formatBuildingIds(item.building_ids)}
                  </>
                }
                primaryTypographyProps={{ noWrap: true }}
              />
              <StatusChip status={item.status} />
            </Stack>

            {(item.status === 'uploading' || item.status === 'pending') && (
              <Box sx={{ width: '100%', mt: 1 }}>
                <LinearProgress variant={item.status === 'uploading' ? 'determinate' : 'indeterminate'} value={item.progress} />
                {item.status === 'uploading' && (
                  <Typography variant="caption" color="text.secondary">
                    {item.progress}%
                  </Typography>
                )}
              </Box>
            )}

            {item.message && (
              <Typography
                variant="caption"
                color={item.status === 'success' ? 'success.main' : 'error.main'}
                sx={{ mt: 0.5, display: 'block' }}
              >
                {item.message}
              </Typography>
            )}
          </ListItem>
        ))}
      </List>
    </>
  );
}

/** Floating button + modal — mount once in app layout when provider is at root. */
export function CityBillsUploadProgressList() {
  const { items } = useCityBillsUpload();
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;

  const activeCount = items.filter((i) => i.status === 'pending' || i.status === 'uploading').length;

  return (
    <>
      <Badge
        badgeContent={items.length}
        color="error"
        overlap="circular"
        max={99}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: (theme) => theme.zIndex.snackbar,
          '& .MuiBadge-badge': { zIndex: (theme) => theme.zIndex.snackbar + 1 }
        }}
      >
        <Fab color="primary" aria-label="View upload progress" onClick={() => setOpen(true)}>
          <CloudUploadOutlined style={{ fontSize: 20 }} />
        </Fab>
      </Badge>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth aria-labelledby="city-bills-upload-progress-title">
        <DialogTitle id="city-bills-upload-progress-title" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="span">
              Upload progress
            </Typography>
            {activeCount > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({activeCount} active)
              </Typography>
            )}
          </Box>
          <IconButton aria-label="close" onClick={() => setOpen(false)}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <UploadProgressListContent />
        </DialogContent>
      </Dialog>
    </>
  );
}

function CityBillsUploaderInner({
  company_id,
  property_id,
  building_ids,
  maxFiles,
  showProgressList = true
}: CityBillsUploaderProps & { showProgressList?: boolean }) {
  const { items, enqueueFiles, maxFiles: limit } = useCityBillsUpload();
  const slotsLeft = limit - items.length;
  const atLimit = slotsLeft <= 0;

  const { showError } = useSnackbar();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (atLimit) return;
      enqueueFiles(acceptedFiles, { company_id, property_id, building_ids });
    },
    [atLimit, enqueueFiles, company_id, property_id, building_ids]
  );

  const onDropRejected = useCallback(() => {
    showError('Only PDF files are allowed');
  }, [showError]);

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: PDF_ACCEPT,
    maxFiles: slotsLeft,
    disabled: atLimit,
    noClick: true,
    noKeyboard: true
  });

  const borderColor = useMemo(() => {
    if (isDragReject) return 'error.main';
    if (isDragActive) return 'primary.main';
    if (atLimit) return 'action.disabled';
    return 'divider';
  }, [isDragReject, isDragActive, atLimit]);

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: 2,
          borderStyle: 'dashed',
          borderColor,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: atLimit ? 'not-allowed' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.default',
          transition: 'border-color 0.2s, background-color 0.2s'
        }}
        onClick={atLimit ? undefined : open}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            fontSize: 48,
            color: 'text.secondary',
            mb: 1,
            lineHeight: 0,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <CloudUploadOutlined />
        </Box>
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop PDF files here' : 'Drag & drop PDF bills here'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          or click to select files (max {maxFiles ?? DEFAULT_MAX_FILES}, 3 MB each)
        </Typography>
        <Button
          variant="contained"
          disabled={atLimit}
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
        >
          Select PDF files
        </Button>
        {atLimit && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
            Maximum of {maxFiles ?? DEFAULT_MAX_FILES} files reached
          </Typography>
        )}
        {!atLimit && slotsLeft < (maxFiles ?? DEFAULT_MAX_FILES) && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {slotsLeft} slot{slotsLeft !== 1 ? 's' : ''} remaining
          </Typography>
        )}
      </Box>
      {showProgressList && <CityBillsUploadProgressList />}
    </Box>
  );
}

export function CityBillsUploader({
  company_id,
  property_id,
  building_ids,
  maxFiles = DEFAULT_MAX_FILES,
  apiBaseUrl
}: CityBillsUploaderProps) {
  const existing = useCityBillsUploadOptional();

  const inner = (
    <CityBillsUploaderInner
      company_id={company_id}
      property_id={property_id}
      building_ids={building_ids}
      maxFiles={maxFiles}
      showProgressList={!!existing}
    />
  );

  if (existing) {
    return inner;
  }

  return (
    <CityBillsUploadProvider maxFiles={maxFiles} apiBaseUrl={apiBaseUrl}>
      <CityBillsUploaderInner
        company_id={company_id}
        property_id={property_id}
        building_ids={building_ids}
        maxFiles={maxFiles}
        showProgressList
      />
    </CityBillsUploadProvider>
  );
}

export default CityBillsUploader;
